import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Zap, 
  Layers, 
  Maximize2, 
  Check, 
  ChevronDown, 
  ChevronUp,
  Menu, 
  X, 
  Shield, 
  Clock, 
  FileImage,
  Sparkles,
  ArrowRight,
  Star,
  Upload,
  Download,
  Trash2,
  RefreshCw,
  Eye,
  LogIn,
  LogOut,
  User,
  ArrowLeft,
  ImageIcon,
  Wand2,
  Eraser,
  Move,
  Sun,
  Contrast,
  Palette,
  Thermometer,
  RotateCcw,
  Crown,
  CreditCard,
  RotateCw,
  FlipHorizontal,
  FlipVertical,
  Crop,
  Undo2,
  Film,
  Save,
  Camera,
  ExternalLink,
  FolderOpen,
  Share2,
  Link,
  Globe,
  Lock,
  Copy,
  Play,
  Pause
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Checkbox } from "./components/ui/checkbox";
import { Slider } from "./components/ui/slider";
import { Toaster, toast } from "sonner";
import { ReactCompareSlider, ReactCompareSliderImage } from "react-compare-slider";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies with every request
axios.defaults.withCredentials = true;

// ============== AUTH CONTEXT ==============
const useAuth = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Cookie is sent automatically with withCredentials
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setToken("cookie-auth");
      } catch {
        setUser(null);
        setToken(null);
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    // Cookie is set automatically by the backend (httpOnly)
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (email, password, name, ageConfirmed = false) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, age_confirmed: ageConfirmed });
    // Cookie is set automatically by the backend (httpOnly)
    setToken(response.data.access_token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      // Ignore errors during logout
    }
    setToken(null);
    setUser(null);
  };

  return { user, token, loading, login, register, logout };
};

// ============== ANIMATION VARIANTS ==============
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

// ============== NAVBAR COMPONENT ==============
const Navbar = ({ user, onLogout, onNavigate, currentPage }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = currentPage === "landing" ? [
    { name: "Features", href: "#features" },
    { name: "Pricing", href: "#pricing" },
    { name: "FAQ", href: "#faq" }
  ] : [];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || currentPage !== "landing" ? "bg-obsidian-900/90 backdrop-blur-xl border-b border-white/5" : ""
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button 
            onClick={() => onNavigate("landing")} 
            className="flex items-center gap-3"
            data-testid="logo"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-outfit font-bold text-xl text-white">AI Image Suite</span>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="nav-link text-slate-300 hover:text-white transition-colors font-medium"
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <span className="text-slate-400 text-sm">
                  <User className="w-4 h-4 inline mr-2" />
                  {user.name}
                </span>
                <Button
                  className="btn-gradient text-white rounded-full px-6 shadow-lg shadow-blue-500/25"
                  onClick={() => onNavigate("dashboard")}
                  data-testid="nav-dashboard-btn"
                >
                  Dashboard
                </Button>
                <Button
                  variant="ghost"
                  className="text-slate-300 hover:text-white hover:bg-white/5"
                  onClick={onLogout}
                  data-testid="nav-logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  className="text-slate-300 hover:text-white hover:bg-white/5"
                  onClick={() => onNavigate("login")}
                  data-testid="nav-login-btn"
                >
                  Log In
                </Button>
                <Button
                  className="btn-gradient text-white rounded-full px-6 shadow-lg shadow-blue-500/25"
                  onClick={() => onNavigate("dashboard")}
                  data-testid="nav-cta-btn"
                >
                  Get Started Free
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-obsidian-900/98 backdrop-blur-xl border-t border-white/5"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="block text-slate-300 hover:text-white py-2 font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              ))}
              <div className="pt-4 space-y-3">
                {user ? (
                  <>
                    <Button
                      className="w-full btn-gradient text-white"
                      onClick={() => { onNavigate("dashboard"); setIsMobileMenuOpen(false); }}
                    >
                      Dashboard
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full border-white/10 text-white hover:bg-white/5"
                      onClick={() => { onLogout(); setIsMobileMenuOpen(false); }}
                    >
                      Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      className="w-full border-white/10 text-white hover:bg-white/5"
                      onClick={() => { onNavigate("login"); setIsMobileMenuOpen(false); }}
                    >
                      Log In
                    </Button>
                    <Button
                      className="w-full btn-gradient text-white"
                      onClick={() => { onNavigate("dashboard"); setIsMobileMenuOpen(false); }}
                    >
                      Get Started Free
                    </Button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// ============== AUTH PAGE ==============
const AuthPage = ({ onLogin, onRegister, onNavigate, isLogin = true }) => {
  const [isLoginMode, setIsLoginMode] = useState(isLogin);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isLoginMode && !ageConfirmed) {
      toast.error("You must confirm you are 18 or older to create an account");
      return;
    }
    setLoading(true);
    try {
      if (isLoginMode) {
        await onLogin(email, password);
        toast.success("Welcome back!");
      } else {
        await onRegister(email, password, name, ageConfirmed);
        toast.success("Account created successfully!");
      }
      onNavigate("dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-20" data-testid="auth-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="glass-card rounded-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-outfit font-bold text-2xl text-white mb-2">
              {isLoginMode ? "Welcome Back" : "Create Account"}
            </h1>
            <p className="text-slate-400 text-sm">
              {isLoginMode ? "Sign in to continue to your dashboard" : "Get started with AI Image Suite"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLoginMode && (
              <div>
                <Label htmlFor="name" className="text-slate-300">Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="mt-1 bg-obsidian-800 border-slate-700 text-white"
                  required={!isLoginMode}
                  data-testid="auth-name-input"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1 bg-obsidian-800 border-slate-700 text-white"
                required
                data-testid="auth-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1 bg-obsidian-800 border-slate-700 text-white"
                required
                data-testid="auth-password-input"
              />
            </div>
            {!isLoginMode && (
              <div className="flex items-start gap-3 mt-2">
                <Checkbox
                  id="age-confirm"
                  checked={ageConfirmed}
                  onCheckedChange={setAgeConfirmed}
                  className="mt-0.5 border-slate-600 data-[state=checked]:bg-blue-600"
                  data-testid="age-confirm-checkbox"
                />
                <Label htmlFor="age-confirm" className="text-slate-400 text-xs leading-relaxed cursor-pointer">
                  I confirm I am at least 18 years old and agree to the{" "}
                  <button type="button" onClick={() => onNavigate("terms")} className="text-blue-400 hover:text-blue-300 underline">
                    Terms of Service
                  </button>{" "}
                  and{" "}
                  <button type="button" onClick={() => onNavigate("privacy")} className="text-blue-400 hover:text-blue-300 underline">
                    Privacy Policy
                  </button>, including the prohibition of underage content.
                </Label>
              </div>
            )}
            <Button
              type="submit"
              className="w-full btn-gradient text-white rounded-full py-6 mt-6"
              disabled={loading || (!isLoginMode && !ageConfirmed)}
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : isLoginMode ? (
                "Sign In"
              ) : (
                "Create Account"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLoginMode(!isLoginMode)}
              className="text-blue-400 hover:text-blue-300 text-sm"
              data-testid="auth-toggle-btn"
            >
              {isLoginMode ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>

          <div className="mt-4 text-center">
            <button
              onClick={() => onNavigate("dashboard")}
              className="text-slate-500 hover:text-slate-400 text-sm"
              data-testid="auth-skip-btn"
            >
              Continue without account (Free tier)
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ============== DASHBOARD PAGE ==============
const DashboardPage = ({ user, token, onNavigate }) => {
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [operations, setOperations] = useState({
    rename: true,
    remove_bg: false,
    resize: false
  });
  const [resizeOptions, setResizeOptions] = useState({
    width: "",
    height: "",
    percentage: ""
  });
  const [showPreview, setShowPreview] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  
  // Batch processing state
  const [batchMode, setBatchMode] = useState(false);
  const [batchDescription, setBatchDescription] = useState("");
  const [batchSize, setBatchSize] = useState(10); // 10 or 20
  const [batchProcessing, setBatchProcessing] = useState(false);
  
  // Smart Batch state
  const [smartBatchEnabled, setSmartBatchEnabled] = useState(false);
  const [smartMode, setSmartMode] = useState("auto");
  const [smartContext, setSmartContext] = useState("");
  const [smartAnalysis, setSmartAnalysis] = useState(null);
  const [smartAnalyzing, setSmartAnalyzing] = useState(false);

  // Credit system state
  const [credits, setCredits] = useState({
    available: 50,
    used: 0,
    total: 50
  });
  const [planInfo, setPlanInfo] = useState({
    name: "starter",
    monthly_allocation: 50,
    rate_limits: { per_minute: 5, per_hour: 30, per_day: 100 },
    max_batch_size: 5
  });
  const [creditCosts, setCreditCosts] = useState({
    rename: 1, resize: 1, remove_bg: 5, touchup: 3, auto_enhance: 4, filter: 2
  });

  // Touch-up state
  const [touchUpSettings, setTouchUpSettings] = useState({
    brightness: 1.0,
    contrast: 1.0,
    saturation: 1.0,
    warmth: 0.0
  });
  const [touchUpUsage, setTouchUpUsage] = useState({ used: 0, limit: 10, remaining: 10, unlimited: false });
  const [applyingTouchUp, setApplyingTouchUp] = useState(false);
  const [showTouchUp, setShowTouchUp] = useState(false);

  // Filter presets state
  const [showFilters, setShowFilters] = useState(false);
  const [applyingFilter, setApplyingFilter] = useState(false);
  const FILTER_PRESETS = [
    { name: "vintage", label: "Vintage", color: "from-amber-500 to-orange-600" },
    { name: "bw", label: "B&W", color: "from-gray-500 to-gray-700" },
    { name: "sepia", label: "Sepia", color: "from-yellow-600 to-amber-700" },
    { name: "vivid", label: "Vivid", color: "from-pink-500 to-rose-600" },
    { name: "muted", label: "Muted", color: "from-slate-400 to-slate-600" },
    { name: "cinematic", label: "Cinematic", color: "from-teal-500 to-cyan-700" }
  ];

  // Rotate/Crop state
  const [showEditTools, setShowEditTools] = useState(false);
  
  // Crop tool state
  const [cropMode, setCropMode] = useState(false);
  const [cropSelection, setCropSelection] = useState(null); // { x, y, width, height }
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [imageNaturalSize, setImageNaturalSize] = useState({ width: 0, height: 0 });
  const [applyingCrop, setApplyingCrop] = useState(false);

  // Comparison slider state
  const [showCompareSlider, setShowCompareSlider] = useState(false);

  // Download quality state
  const [downloadQuality, setDownloadQuality] = useState(95);

  // Batch progress state
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0, status: "" });

  // Gallery state
  const [activeTab, setActiveTab] = useState("editor"); // "editor" or "gallery"
  const [gallery, setGallery] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [savingToGallery, setSavingToGallery] = useState(false);
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // Batch limits
  const MAX_BATCH_SIZE = 20;
  const MAX_FILE_SIZE_MB = 10;
  const MAX_TOTAL_SIZE_MB = 100;

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await handleFileUpload(e.dataTransfer.files);
    }
  }, []); // handleFileUpload is stable within component scope

  const handleFileUpload = async (files) => {
    const fileArray = Array.from(files);
    
    // Check batch limits
    if (fileArray.length > MAX_BATCH_SIZE) {
      toast.error(`Maximum ${MAX_BATCH_SIZE} images per batch. You selected ${fileArray.length}.`);
      return;
    }
    
    // Check total size
    let totalSize = 0;
    for (const file of fileArray) {
      const sizeMB = file.size / (1024 * 1024);
      if (sizeMB > MAX_FILE_SIZE_MB) {
        toast.error(`${file.name} is too large (${sizeMB.toFixed(1)}MB). Max ${MAX_FILE_SIZE_MB}MB per file.`);
        return;
      }
      totalSize += sizeMB;
    }
    
    if (totalSize > MAX_TOTAL_SIZE_MB) {
      toast.error(`Total batch size (${totalSize.toFixed(1)}MB) exceeds ${MAX_TOTAL_SIZE_MB}MB limit.`);
      return;
    }
    
    // Enable batch mode if multiple files
    if (fileArray.length > 1) {
      setBatchMode(true);
    }
    
    for (const file of fileArray) {
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
        toast.error(`${file.name} is not a supported format`);
        continue;
      }

      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await axios.post(`${API}/images/upload`, formData, {
          headers: { ...headers, "Content-Type": "multipart/form-data" }
        });
        
        setImages(prev => [...prev, response.data]);
        // Auto-select the last uploaded image
        setSelectedImage(response.data);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    toast.success(`${fileArray.length} image(s) uploaded successfully`);
  };

  // Smart Batch Analysis
  const handleSmartAnalyze = async () => {
    if (images.length === 0) {
      toast.error("No images to analyze");
      return;
    }

    setSmartAnalyzing(true);
    try {
      const response = await axios.post(`${API}/batch/images/smart-organize`, {
        image_ids: images.map(img => img.image_id),
        smart_mode: smartMode,
        smart_context: smartContext || null
      }, { headers });

      setSmartAnalysis(response.data);
      toast.success(`Smart organizer found ${response.data.groups?.length || 0} group(s)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Smart analysis failed");
    } finally {
      setSmartAnalyzing(false);
    }
  };

  // Batch process all images
  const handleBatchProcess = async () => {
    if (images.length === 0) {
      toast.error("No images to process");
      return;
    }

    const ops = [];
    if (operations.rename) ops.push("rename");
    if (operations.remove_bg) ops.push("remove_bg");
    if (operations.resize) ops.push("resize");

    if (ops.length === 0) {
      toast.error("Please select at least one operation");
      return;
    }

    setBatchProcessing(true);
    try {
      const response = await axios.post(`${API}/batch/images/process`, {
        image_ids: images.map(img => img.image_id),
        operations: ops,
        resize_width: resizeOptions.width ? parseInt(resizeOptions.width) : null,
        resize_height: resizeOptions.height ? parseInt(resizeOptions.height) : null,
        resize_percentage: resizeOptions.percentage ? parseInt(resizeOptions.percentage) : null,
        batch_description: smartBatchEnabled ? null : (batchDescription || null),
        use_smart_batch: smartBatchEnabled,
        smart_mode: smartMode,
        smart_context: smartContext || null
      }, { headers });

      // Update images with processed data
      const processedMap = {};
      response.data.results.forEach(result => {
        processedMap[result.image_id] = result;
      });

      // Store smart analysis if returned
      if (response.data.smart_analysis) {
        setSmartAnalysis(response.data.smart_analysis);
      }

      // Refresh each image's data
      const updatedImages = await Promise.all(
        images.map(async (img) => {
          try {
            const imgResponse = await axios.get(`${API}/images/${img.image_id}`, { headers });
            return imgResponse.data;
          } catch {
            return img;
          }
        })
      );

      setImages(updatedImages);
      if (updatedImages.length > 0) {
        setSelectedImage(updatedImages[0]);
        setShowPreview(true);
      }

      toast.success(`Batch processed: ${response.data.total_processed} succeeded, ${response.data.total_failed} failed`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Batch processing failed");
    } finally {
      setBatchProcessing(false);
      setBatchProgress({ current: 0, total: 0, status: "" });
    }
  };

  // Download all processed images as zip
  const handleBatchDownload = async () => {
    const processedImages = images.filter(img => img.processed_base64);
    if (processedImages.length === 0) {
      toast.error("No processed images to download");
      return;
    }

    try {
      const imageIds = processedImages.map(img => img.image_id).join(",");
      const response = await axios.get(
        `${API}/batch/images/download?image_ids=${imageIds}&processed=true&quality=${downloadQuality}`,
        { headers, responseType: "blob" }
      );

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "batch_download.zip";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Downloaded ${processedImages.length} images as zip`);
    } catch (error) {
      toast.error("Batch download failed");
    }
  };

  // Touch-up functions
  const fetchTouchUpUsage = async () => {
    try {
      const response = await axios.get(`${API}/touchup/usage`, { headers });
      setTouchUpUsage(response.data);
    } catch (error) {
      console.error("Failed to fetch touch-up usage");
    }
  };

  // Credit system functions
  const fetchCredits = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/credits`, { headers });
      setCredits(response.data.credits);
      setPlanInfo(response.data.plan);
      setCreditCosts(response.data.credit_costs);
    } catch (error) {
      console.error("Failed to fetch credits");
    }
  };

  const handleTouchUpAdjust = async () => {
    if (!selectedImage) return;
    
    setApplyingTouchUp(true);
    try {
      const response = await axios.post(`${API}/touchup/adjust`, {
        image_id: selectedImage.image_id,
        ...touchUpSettings
      }, { headers });

      // Update image
      setSelectedImage(prev => ({
        ...prev,
        processed_base64: response.data.processed_base64,
        status: "processed"
      }));
      setImages(prev => prev.map(img => 
        img.image_id === selectedImage.image_id 
          ? { ...img, processed_base64: response.data.processed_base64, status: "processed" }
          : img
      ));
      setTouchUpUsage(response.data.usage);
      setShowPreview(true);
      toast.success("Adjustments applied!");
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error(error.response.data.detail.message || "Touch-up limit reached. Upgrade to continue!");
      } else {
        toast.error("Failed to apply adjustments");
      }
    } finally {
      setApplyingTouchUp(false);
    }
  };

  const handleAutoEnhance = async () => {
    if (!selectedImage) return;
    
    setApplyingTouchUp(true);
    try {
      const response = await axios.post(`${API}/touchup/auto-enhance`, {
        image_id: selectedImage.image_id
      }, { headers });

      // Update image
      setSelectedImage(prev => ({
        ...prev,
        processed_base64: response.data.processed_base64,
        status: "processed"
      }));
      setImages(prev => prev.map(img => 
        img.image_id === selectedImage.image_id 
          ? { ...img, processed_base64: response.data.processed_base64, status: "processed" }
          : img
      ));
      setTouchUpUsage(response.data.usage);
      setShowPreview(true);
      toast.success("Auto-enhance applied!");
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error(error.response.data.detail.message || "Touch-up limit reached. Upgrade to continue!");
      } else {
        toast.error("Failed to auto-enhance");
      }
    } finally {
      setApplyingTouchUp(false);
    }
  };

  const handleResetTouchUp = async () => {
    if (!selectedImage) return;
    
    try {
      const response = await axios.post(`${API}/touchup/reset`, {
        image_id: selectedImage.image_id
      }, { headers });

      // Reset settings
      setTouchUpSettings({ brightness: 1.0, contrast: 1.0, saturation: 1.0, warmth: 0.0 });
      
      // Update image
      setSelectedImage(prev => ({
        ...prev,
        processed_base64: null,
        status: "uploaded"
      }));
      setImages(prev => prev.map(img => 
        img.image_id === selectedImage.image_id 
          ? { ...img, processed_base64: null, status: "uploaded" }
          : img
      ));
      setShowPreview(false);
      setShowCompareSlider(false);
      toast.success("Reset to original");
    } catch (error) {
      toast.error("Failed to reset image");
    }
  };

  // Apply filter preset
  const handleApplyFilter = async (filterName) => {
    if (!selectedImage) return;
    
    setApplyingFilter(true);
    try {
      const response = await axios.post(`${API}/filters/apply`, {
        image_id: selectedImage.image_id,
        filter_name: filterName
      }, { headers });

      setSelectedImage(prev => ({
        ...prev,
        processed_base64: response.data.processed_base64,
        status: "processed"
      }));
      setImages(prev => prev.map(img => 
        img.image_id === selectedImage.image_id 
          ? { ...img, processed_base64: response.data.processed_base64, status: "processed" }
          : img
      ));
      setTouchUpUsage(response.data.usage);
      setShowPreview(true);
      toast.success(`${filterName.charAt(0).toUpperCase() + filterName.slice(1)} filter applied!`);
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error("Touch-up limit reached. Upgrade to continue!");
      } else {
        toast.error("Failed to apply filter");
      }
    } finally {
      setApplyingFilter(false);
    }
  };

  // Rotate image
  const handleRotate = async (angle) => {
    if (!selectedImage) return;
    
    try {
      const response = await axios.post(`${API}/edit/rotate`, {
        image_id: selectedImage.image_id,
        angle: angle,
        flip_horizontal: false,
        flip_vertical: false
      }, { headers });

      setSelectedImage(prev => ({
        ...prev,
        processed_base64: response.data.processed_base64,
        status: "processed"
      }));
      setImages(prev => prev.map(img => 
        img.image_id === selectedImage.image_id 
          ? { ...img, processed_base64: response.data.processed_base64, status: "processed" }
          : img
      ));
      setShowPreview(true);
      toast.success(`Rotated ${angle}°`);
    } catch (error) {
      toast.error("Failed to rotate image");
    }
  };

  // Flip image
  const handleFlip = async (horizontal) => {
    if (!selectedImage) return;
    
    try {
      const response = await axios.post(`${API}/edit/rotate`, {
        image_id: selectedImage.image_id,
        angle: 0,
        flip_horizontal: horizontal,
        flip_vertical: !horizontal
      }, { headers });

      setSelectedImage(prev => ({
        ...prev,
        processed_base64: response.data.processed_base64,
        status: "processed"
      }));
      setImages(prev => prev.map(img => 
        img.image_id === selectedImage.image_id 
          ? { ...img, processed_base64: response.data.processed_base64, status: "processed" }
          : img
      ));
      setShowPreview(true);
      toast.success(`Flipped ${horizontal ? "horizontally" : "vertically"}`);
    } catch (error) {
      toast.error("Failed to flip image");
    }
  };

  // Undo last edit
  const handleUndo = async () => {
    if (!selectedImage) return;
    
    try {
      const response = await axios.post(`${API}/edit/undo`, {
        image_id: selectedImage.image_id
      }, { headers });

      setSelectedImage(prev => ({
        ...prev,
        processed_base64: response.data.processed_base64,
        status: response.data.processed_base64 ? "processed" : "uploaded"
      }));
      setImages(prev => prev.map(img => 
        img.image_id === selectedImage.image_id 
          ? { ...img, processed_base64: response.data.processed_base64, status: response.data.processed_base64 ? "processed" : "uploaded" }
          : img
      ));
      if (!response.data.processed_base64) {
        setShowPreview(false);
        setShowCompareSlider(false);
      }
      toast.success("Undone!");
    } catch (error) {
      toast.error("Nothing to undo");
    }
  };

  // Crop tool handlers
  const handleStartCrop = () => {
    setCropMode(true);
    setCropSelection(null);
    setShowCompareSlider(false); // Can't crop in compare mode
    toast.info("Click and drag on the image to select crop area");
  };

  const handleCancelCrop = () => {
    setCropMode(false);
    setCropSelection(null);
    setIsDragging(false);
    setDragStart(null);
  };

  const handleCropMouseDown = (e) => {
    if (!cropMode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setDragStart({ x, y });
    setIsDragging(true);
    setCropSelection({ x, y, width: 0, height: 0 });
  };

  const handleCropMouseMove = (e) => {
    if (!isDragging || !dragStart) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const currentX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const currentY = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
    
    const x = Math.min(dragStart.x, currentX);
    const y = Math.min(dragStart.y, currentY);
    const width = Math.abs(currentX - dragStart.x);
    const height = Math.abs(currentY - dragStart.y);
    
    setCropSelection({ x, y, width, height });
  };

  const handleCropMouseUp = () => {
    setIsDragging(false);
    if (cropSelection && cropSelection.width < 10 && cropSelection.height < 10) {
      setCropSelection(null);
      toast.warning("Selection too small. Please drag to select a larger area.");
    }
  };

  const handleApplyCrop = async () => {
    if (!selectedImage || !cropSelection || !imageNaturalSize.width) {
      toast.error("Please select a crop area first");
      return;
    }

    setApplyingCrop(true);

    try {
      // Get the display element dimensions
      const previewElement = document.getElementById("crop-preview-container");
      if (!previewElement) {
        throw new Error("Preview element not found");
      }

      const displayWidth = previewElement.offsetWidth;
      const displayHeight = previewElement.offsetHeight;

      // Calculate scale factor from display to actual image
      const scaleX = imageNaturalSize.width / displayWidth;
      const scaleY = imageNaturalSize.height / displayHeight;

      // Convert selection coordinates to actual image coordinates
      const actualX = Math.round(cropSelection.x * scaleX);
      const actualY = Math.round(cropSelection.y * scaleY);
      const actualWidth = Math.round(cropSelection.width * scaleX);
      const actualHeight = Math.round(cropSelection.height * scaleY);

      const response = await axios.post(`${API}/edit/crop`, {
        image_id: selectedImage.image_id,
        x: actualX,
        y: actualY,
        width: actualWidth,
        height: actualHeight
      }, { headers });

      setSelectedImage(prev => ({
        ...prev,
        processed_base64: response.data.processed_base64,
        status: "processed"
      }));
      setImages(prev => prev.map(img => 
        img.image_id === selectedImage.image_id 
          ? { ...img, processed_base64: response.data.processed_base64, status: "processed" }
          : img
      ));
      
      setShowPreview(true);
      setCropMode(false);
      setCropSelection(null);
      toast.success("Image cropped successfully!");
    } catch (error) {
      console.error("Crop error:", error);
      toast.error("Failed to crop image");
    } finally {
      setApplyingCrop(false);
    }
  };

  // Get natural image dimensions when selected image changes
  useEffect(() => {
    if (selectedImage) {
      const img = new Image();
      img.onload = () => {
        setImageNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.src = `data:image/png;base64,${
        selectedImage.processed_base64 || selectedImage.original_base64
      }`;
    }
  }, [selectedImage, selectedImage?.processed_base64]);

  // Fetch usage on mount
  useEffect(() => {
    fetchTouchUpUsage();
    if (token) {
      fetchCredits();
    }
  }, [token]);

  // Fetch gallery on mount if user is logged in
  useEffect(() => {
    if (user && token) {
      fetchGallery();
    }
  }, [user, token]);

  // Gallery functions
  const fetchGallery = async () => {
    if (!token) return;
    setGalleryLoading(true);
    try {
      const response = await axios.get(`${API}/gallery`, { headers });
      setGallery(response.data.images || []);
    } catch (error) {
      console.error("Failed to fetch gallery:", error);
    } finally {
      setGalleryLoading(false);
    }
  };

  const handleSaveToGallery = async (title = null) => {
    if (!selectedImage) {
      toast.error("Please select an image first");
      return;
    }
    if (!user) {
      toast.error("Please log in to save images to your gallery");
      return;
    }

    setSavingToGallery(true);
    try {
      await axios.post(`${API}/gallery/save`, {
        image_id: selectedImage.image_id,
        title: title || selectedImage.new_name || selectedImage.original_name
      }, { headers });
      
      toast.success("Image saved to gallery!");
      fetchGallery();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save to gallery");
    } finally {
      setSavingToGallery(false);
    }
  };

  const handleDeleteFromGallery = async (galleryId) => {
    try {
      await axios.delete(`${API}/gallery/${galleryId}`, { headers });
      setGallery(prev => prev.filter(img => img.id !== galleryId));
      if (selectedGalleryImage?.id === galleryId) {
        setSelectedGalleryImage(null);
      }
      toast.success("Image removed from gallery");
    } catch (error) {
      toast.error("Failed to delete image");
    }
  };

  const handleTogglePublic = async (galleryId, currentStatus) => {
    try {
      await axios.patch(`${API}/gallery/${galleryId}?is_public=${!currentStatus}`, {}, { headers });
      setGallery(prev => prev.map(img => 
        img.id === galleryId ? { ...img, is_public: !currentStatus } : img
      ));
      toast.success(currentStatus ? "Image is now private" : "Image is now public");
    } catch (error) {
      toast.error("Failed to update visibility");
    }
  };

  const getShareLink = () => {
    if (!user) return "";
    return `${window.location.origin}?gallery=${user.id}`;
  };

  const copyShareLink = () => {
    const link = getShareLink();
    navigator.clipboard.writeText(link);
    toast.success("Gallery link copied to clipboard!");
  };

  const handleProcess = async () => {
    if (!selectedImage) {
      toast.error("Please select an image first");
      return;
    }

    const ops = [];
    if (operations.rename) ops.push("rename");
    if (operations.remove_bg) ops.push("remove_bg");
    if (operations.resize) ops.push("resize");

    if (ops.length === 0) {
      toast.error("Please select at least one operation");
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${API}/images/process`, {
        image_id: selectedImage.image_id,
        operations: ops,
        resize_width: resizeOptions.width ? parseInt(resizeOptions.width) : null,
        resize_height: resizeOptions.height ? parseInt(resizeOptions.height) : null,
        resize_percentage: resizeOptions.percentage ? parseInt(resizeOptions.percentage) : null
      }, { headers });

      // Update image in list
      setImages(prev => prev.map(img => 
        img.image_id === selectedImage.image_id ? response.data : img
      ));
      setSelectedImage(response.data);
      setShowPreview(true);
      toast.success("Image processed successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Processing failed");
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = async (imageId, processed = true) => {
    try {
      const response = await axios.get(
        `${API}/images/${imageId}/download?processed=${processed}&quality=${downloadQuality}`,
        { headers, responseType: "blob" }
      );
      
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      
      // Get filename from content-disposition header
      const contentDisposition = response.headers["content-disposition"];
      let filename = "downloaded_image";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Image downloaded!");
    } catch (error) {
      toast.error("Download failed");
    }
  };

  const handleDelete = async (imageId) => {
    try {
      await axios.delete(`${API}/images/${imageId}`, { headers });
      setImages(prev => prev.filter(img => img.image_id !== imageId));
      if (selectedImage?.image_id === imageId) {
        setSelectedImage(null);
        setShowPreview(false);
      }
      toast.success("Image deleted");
    } catch (error) {
      toast.error("Failed to delete image");
    }
  };

  // Delete all images with double-click confirmation
  const [deleteAllClicks, setDeleteAllClicks] = useState(0);
  const [deleteAllTimer, setDeleteAllTimer] = useState(null);
  
  // Multi-select state
  const [selectedImageIds, setSelectedImageIds] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);

  const toggleImageSelection = (imageId, e) => {
    e.stopPropagation();
    setSelectedImageIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
      } else {
        newSet.add(imageId);
      }
      return newSet;
    });
  };

  const selectAllImages = () => {
    if (selectedImageIds.size === images.length) {
      // Deselect all
      setSelectedImageIds(new Set());
    } else {
      // Select all
      setSelectedImageIds(new Set(images.map(img => img.image_id)));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedImageIds.size === 0) return;
    
    const count = selectedImageIds.size;
    try {
      await Promise.all(
        Array.from(selectedImageIds).map(id => 
          axios.delete(`${API}/images/${id}`, { headers }).catch(() => {})
        )
      );
      
      setImages(prev => prev.filter(img => !selectedImageIds.has(img.image_id)));
      
      if (selectedImage && selectedImageIds.has(selectedImage.image_id)) {
        setSelectedImage(null);
        setShowPreview(false);
      }
      
      setSelectedImageIds(new Set());
      setSelectionMode(false);
      toast.success(`Deleted ${count} images`);
    } catch (error) {
      toast.error("Failed to delete some images");
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (selectedImage) handleUndo();
      }
      // Ctrl+A for select all (when in selection mode)
      if ((e.ctrlKey || e.metaKey) && e.key === 'a' && selectionMode) {
        e.preventDefault();
        selectAllImages();
      }
      // Delete key for delete selected
      if (e.key === 'Delete' && selectedImageIds.size > 0) {
        handleDeleteSelected();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage, selectionMode, selectedImageIds]);

  const handleDeleteAll = async () => {
    if (deleteAllClicks === 0) {
      // First click - show warning
      setDeleteAllClicks(1);
      toast.warning("Click again to delete all images", { duration: 2000 });
      
      // Reset after 2 seconds if no second click
      const timer = setTimeout(() => {
        setDeleteAllClicks(0);
      }, 2000);
      setDeleteAllTimer(timer);
    } else {
      // Second click - delete all
      clearTimeout(deleteAllTimer);
      setDeleteAllClicks(0);
      
      try {
        // Delete all images
        await Promise.all(images.map(img => 
          axios.delete(`${API}/images/${img.image_id}`, { headers }).catch(() => {})
        ));
        
        setImages([]);
        setSelectedImage(null);
        setShowPreview(false);
        setBatchMode(false);
        setBatchDescription("");
        toast.success(`Deleted ${images.length} images`);
      } catch (error) {
        toast.error("Failed to delete some images");
      }
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-6" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-outfit font-bold text-3xl md:text-4xl text-white mb-2">
            Image Processing Dashboard
          </h1>
          <p className="text-slate-400">
            Upload, process, and download your images with AI-powered tools
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6"
        >
          <div className="flex items-center gap-2 bg-obsidian-800/50 p-1 rounded-xl w-fit">
            <button
              onClick={() => setActiveTab("editor")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === "editor"
                  ? "bg-blue-600 text-white"
                  : "text-slate-400 hover:text-white hover:bg-white/5"
              }`}
              data-testid="tab-editor"
            >
              <Wand2 className="w-4 h-4" />
              Editor
            </button>
            <button
              onClick={() => setActiveTab("gallery")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === "gallery"
                ? "bg-blue-600 text-white"
                : "text-slate-400 hover:text-white hover:bg-white/5"
            }`}
            data-testid="tab-gallery"
          >
            <FolderOpen className="w-4 h-4" />
            My Gallery
            {gallery.length > 0 && (
              <span className="bg-white/20 text-xs px-1.5 py-0.5 rounded-full">
                {gallery.length}
              </span>
            )}
          </button>
          </div>

          {/* Credit Balance Widget */}
          {user && (
            <div className="flex items-center gap-4 px-4 py-2 bg-obsidian-800/50 rounded-xl border border-slate-700/50">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center">
                  <Zap className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">{credits.available} Credits</p>
                  <p className="text-slate-500 text-xs">{planInfo.name.charAt(0).toUpperCase() + planInfo.name.slice(1)} Plan</p>
                </div>
              </div>
              <div className="h-8 w-px bg-slate-700" />
              <div className="text-xs text-slate-400">
                <p>Rename: {creditCosts.rename}cr • BG Remove: {creditCosts.remove_bg}cr</p>
              </div>
              {credits.available < 20 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-blue-500/50 text-blue-400 text-xs"
                  onClick={() => document.getElementById("pricing").scrollIntoView({ behavior: "smooth" })}
                >
                  Get More
                </Button>
              )}
            </div>
          )}
        </motion.div>

        {/* Editor Tab Content */}
        {activeTab === "editor" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Upload & Image List */}
          <div className="lg:col-span-1 space-y-6">
            {/* Upload Area */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className={`glass-card rounded-2xl p-6 border-2 border-dashed transition-colors ${
                dragActive ? "border-blue-500 bg-blue-500/10" : "border-slate-700"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              data-testid="upload-area"
            >
              <input
                type="file"
                id="file-upload"
                multiple
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => handleFileUpload(e.target.files)}
                className="hidden"
                data-testid="file-input"
              />
              <label
                htmlFor="file-upload"
                className="flex flex-col items-center justify-center cursor-pointer py-8"
              >
                <Upload className="w-12 h-12 text-slate-400 mb-4" />
                <p className="text-white font-medium mb-1">Drop images here or click to upload</p>
                <p className="text-slate-500 text-sm">JPG, PNG, WEBP • Up to 20 images • 10MB each</p>
              </label>
              <p className="text-slate-600 text-xs text-center mt-2" data-testid="content-policy-notice">
                <Shield className="w-3 h-3 inline mr-1" />
                By uploading, you confirm these images comply with our{" "}
                <button onClick={() => onNavigate("terms")} className="text-blue-500 hover:text-blue-400 underline">content policy</button>
                {" "}— no underage content permitted.
              </p>
            </motion.div>

            {/* Image List */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass-card rounded-2xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-outfit font-semibold text-lg text-white">
                  Uploaded Images ({images.length})
                </h3>
                {images.length > 0 && (
                  <div className="flex items-center gap-2">
                    {/* Selection Mode Toggle */}
                    <button
                      onClick={() => {
                        setSelectionMode(!selectionMode);
                        if (selectionMode) setSelectedImageIds(new Set());
                      }}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        selectionMode
                          ? "bg-blue-500 text-white"
                          : "bg-blue-500/10 text-blue-400 hover:bg-blue-500/20"
                      }`}
                      data-testid="selection-mode-btn"
                    >
                      <Check className="w-3 h-3" />
                      Select
                    </button>
                    
                    {/* Delete Selected / Delete All */}
                    {selectionMode && selectedImageIds.size > 0 ? (
                      <button
                        onClick={handleDeleteSelected}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500 text-white hover:bg-red-600 transition-all"
                        data-testid="delete-selected-btn"
                      >
                        <Trash2 className="w-3 h-3" />
                        Delete ({selectedImageIds.size})
                      </button>
                    ) : (
                      <button
                        onClick={handleDeleteAll}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          deleteAllClicks === 1
                            ? "bg-red-500 text-white animate-pulse"
                            : "bg-red-500/10 text-red-400 hover:bg-red-500/20"
                        }`}
                        data-testid="delete-all-btn"
                      >
                        <Trash2 className="w-3 h-3" />
                        {deleteAllClicks === 1 ? "Confirm" : "All"}
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {/* Select All checkbox when in selection mode */}
              {selectionMode && images.length > 0 && (
                <div className="flex items-center gap-2 mb-3 pb-3 border-b border-slate-700/50">
                  <Checkbox
                    checked={selectedImageIds.size === images.length && images.length > 0}
                    onCheckedChange={selectAllImages}
                    className="border-slate-600"
                    data-testid="select-all-checkbox"
                  />
                  <span className="text-slate-400 text-sm">
                    Select All ({selectedImageIds.size}/{images.length})
                  </span>
                </div>
              )}
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {images.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No images uploaded yet</p>
                ) : (
                  images.map((img) => (
                    <div
                      key={img.image_id}
                      onClick={() => !selectionMode && setSelectedImage(img)}
                      className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                        selectedImageIds.has(img.image_id)
                          ? "bg-blue-500/30 border border-blue-500/50"
                          : selectedImage?.image_id === img.image_id
                          ? "bg-blue-500/20 border border-blue-500/50"
                          : "bg-obsidian-800/50 hover:bg-obsidian-700/50"
                      }`}
                      data-testid={`image-item-${img.image_id}`}
                    >
                      {/* Selection checkbox */}
                      {selectionMode && (
                        <Checkbox
                          checked={selectedImageIds.has(img.image_id)}
                          onCheckedChange={(e) => toggleImageSelection(img.image_id, { stopPropagation: () => {} })}
                          onClick={(e) => e.stopPropagation()}
                          className="border-slate-600"
                          data-testid={`select-checkbox-${img.image_id}`}
                        />
                      )}
                      <img
                        src={`data:image/png;base64,${img.original_base64}`}
                        alt={img.original_name}
                        className="w-12 h-12 object-cover rounded-lg"
                        onClick={(e) => {
                          if (selectionMode) {
                            e.stopPropagation();
                            toggleImageSelection(img.image_id, e);
                          }
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">
                          {img.new_name || img.original_name}
                        </p>
                        <p className="text-slate-500 text-xs">
                          {img.status === "processed" ? "Processed" : "Ready to process"}
                        </p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(img.image_id); }}
                        className="text-slate-500 hover:text-red-400 p-1"
                        data-testid={`delete-btn-${img.image_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </div>

          {/* Middle Column - Processing Options */}
          <div className="lg:col-span-1 space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass-card rounded-2xl p-6"
            >
              <h3 className="font-outfit font-semibold text-lg text-white mb-6">
                Processing Options
              </h3>

              <div className="space-y-4">
                {/* AI Rename */}
                <div className="flex items-start gap-3 p-4 rounded-xl bg-obsidian-800/50">
                  <Checkbox
                    id="rename"
                    checked={operations.rename}
                    onCheckedChange={(checked) => setOperations(prev => ({ ...prev, rename: checked }))}
                    data-testid="checkbox-rename"
                  />
                  <div className="flex-1">
                    <label htmlFor="rename" className="text-white font-medium cursor-pointer flex items-center gap-2">
                      <Wand2 className="w-4 h-4 text-blue-400" />
                      AI-Powered Renaming
                    </label>
                    <p className="text-slate-500 text-sm mt-1">
                      Generate descriptive 4-word filenames using Gemini 3 Flash
                    </p>
                  </div>
                </div>

                {/* Background Removal */}
                <div className="flex items-start gap-3 p-4 rounded-xl bg-obsidian-800/50">
                  <Checkbox
                    id="remove_bg"
                    checked={operations.remove_bg}
                    onCheckedChange={(checked) => setOperations(prev => ({ ...prev, remove_bg: checked }))}
                    data-testid="checkbox-remove-bg"
                  />
                  <div className="flex-1">
                    <label htmlFor="remove_bg" className="text-white font-medium cursor-pointer flex items-center gap-2">
                      <Eraser className="w-4 h-4 text-violet-400" />
                      Background Removal
                    </label>
                    <p className="text-slate-500 text-sm mt-1">
                      Remove background with AI segmentation, export as PNG
                    </p>
                  </div>
                </div>

                {/* Resize */}
                <div className="p-4 rounded-xl bg-obsidian-800/50">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="resize"
                      checked={operations.resize}
                      onCheckedChange={(checked) => setOperations(prev => ({ ...prev, resize: checked }))}
                      data-testid="checkbox-resize"
                    />
                    <div className="flex-1">
                      <label htmlFor="resize" className="text-white font-medium cursor-pointer flex items-center gap-2">
                        <Move className="w-4 h-4 text-pink-400" />
                        Smart Resizing
                      </label>
                      <p className="text-slate-500 text-sm mt-1">
                        Resize to exact dimensions or by percentage
                      </p>
                    </div>
                  </div>
                  
                  {operations.resize && (
                    <div className="mt-4 pl-8 space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-slate-400 text-xs">Width (px)</Label>
                          <Input
                            type="number"
                            value={resizeOptions.width}
                            onChange={(e) => setResizeOptions(prev => ({ ...prev, width: e.target.value, percentage: "" }))}
                            placeholder="800"
                            className="mt-1 bg-obsidian-900 border-slate-700 text-white"
                            data-testid="resize-width"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-400 text-xs">Height (px)</Label>
                          <Input
                            type="number"
                            value={resizeOptions.height}
                            onChange={(e) => setResizeOptions(prev => ({ ...prev, height: e.target.value, percentage: "" }))}
                            placeholder="600"
                            className="mt-1 bg-obsidian-900 border-slate-700 text-white"
                            data-testid="resize-height"
                          />
                        </div>
                      </div>
                      <div className="text-center text-slate-500 text-xs">or</div>
                      <div>
                        <Label className="text-slate-400 text-xs">Scale (%)</Label>
                        <Input
                          type="number"
                          value={resizeOptions.percentage}
                          onChange={(e) => setResizeOptions(prev => ({ ...prev, percentage: e.target.value, width: "", height: "" }))}
                          placeholder="50"
                          className="mt-1 bg-obsidian-900 border-slate-700 text-white"
                          data-testid="resize-percentage"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Batch Description / Smart Batch Organizer */}
                {images.length > 1 && (
                  <div className="space-y-4">
                    {/* Smart Batch Organizer */}
                    <div className="p-4 rounded-xl bg-gradient-to-r from-violet-500/10 to-purple-500/10 border border-violet-500/20">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <Label className="text-white font-medium flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-violet-400" />
                          Smart Batch Organizer
                        </Label>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={smartBatchEnabled}
                            onCheckedChange={(checked) => setSmartBatchEnabled(!!checked)}
                            data-testid="smart-batch-toggle"
                          />
                          <span className="text-xs text-slate-300">Use AI grouping</span>
                        </div>
                      </div>
                      <p className="text-slate-400 text-xs mb-3">
                        Best for messy work uploads. AI finds where one item ends, starts the next set, and writes clean underscore labels.
                      </p>

                      {smartBatchEnabled && (
                        <>
                          <div className="grid md:grid-cols-2 gap-3 mb-3">
                            <div>
                              <Label className="text-slate-400 text-xs">Mode</Label>
                              <select
                                value={smartMode}
                                onChange={(e) => setSmartMode(e.target.value)}
                                className="mt-1 w-full rounded-lg bg-obsidian-900 border border-slate-700 text-white text-sm px-3 py-2"
                                data-testid="smart-mode-select"
                              >
                                <option value="auto">Auto detect</option>
                                <option value="product_listing">Product listing</option>
                                <option value="car_part">Car part</option>
                                <option value="personal_family">Personal / family</option>
                                <option value="document">Document</option>
                                <option value="mixed_unknown">Mixed / unknown</option>
                              </select>
                            </div>
                            <div className="flex items-end">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={handleSmartAnalyze}
                                disabled={images.length === 0 || smartAnalyzing}
                                className="w-full border-slate-700 text-white hover:bg-white/5"
                                data-testid="smart-analyze-btn"
                              >
                                {smartAnalyzing ? (
                                  <>
                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                    Analyzing...
                                  </>
                                ) : (
                                  <>
                                    <Sparkles className="w-4 h-4 mr-2" />
                                    Preview Grouping
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>

                          <textarea
                            value={smartContext}
                            onChange={(e) => setSmartContext(e.target.value)}
                            placeholder="Optional context: same car with closeups of tires, hubcaps OR me, my mom, and friends at Belmont horse races 1996"
                            className="w-full h-20 px-3 py-2 bg-obsidian-900 border border-slate-700 rounded-lg text-white text-sm placeholder:text-slate-500 resize-none"
                            data-testid="smart-context"
                          />

                          {smartAnalysis?.groups?.length > 0 && (
                            <div className="mt-3 rounded-lg border border-violet-500/20 bg-obsidian-950/60 p-3">
                              <p className="text-xs uppercase tracking-wide text-violet-300 mb-2">Preview</p>
                              <div className="space-y-2 max-h-56 overflow-auto">
                                {smartAnalysis.groups.map((group) => (
                                  <div key={`group-${group.group_number}`} className="rounded-lg border border-slate-800 bg-obsidian-900/80 p-3">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                      <span className="text-white text-sm font-medium">{group.group_name}</span>
                                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-200 border border-violet-500/20">
                                        {group.category}
                                      </span>
                                    </div>
                                    <p className="text-slate-400 text-xs mb-2">{group.reason}</p>
                                    <div className="space-y-1">
                                      {group.photos?.slice(0, 4).map((photo) => (
                                        <div key={photo.image_id} className="text-xs text-slate-300 flex items-center justify-between gap-3">
                                          <span className="truncate">{images.find(img => img.image_id === photo.image_id)?.original_name || photo.image_id}</span>
                                          <code className="text-violet-200">{photo.label}</code>
                                        </div>
                                      ))}
                                      {group.photos?.length > 4 && (
                                        <p className="text-xs text-slate-500">+{group.photos.length - 4} more...</p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Manual Batch Description (shown when Smart Batch is disabled) */}
                    {!smartBatchEnabled && (
                      <div className="p-4 rounded-xl bg-gradient-to-r from-blue-500/10 to-violet-500/10 border border-blue-500/20">
                        <Label className="text-white font-medium flex items-center gap-2 mb-2">
                          <Layers className="w-4 h-4 text-blue-400" />
                          Manual Batch Description (Optional)
                        </Label>
                        <p className="text-slate-400 text-xs mb-3">
                          Describe what&apos;s in your batch. Use <code className="bg-obsidian-800 px-1 rounded">$</code> to separate different items for each image.
                        </p>
                        <textarea
                          value={batchDescription}
                          onChange={(e) => setBatchDescription(e.target.value)}
                          placeholder="Examples:&#10;Simple: Betty, my dog Spot, and I on vacation&#10;Multiple items: brown shoes$ green socks$ blue hat$"
                          className="w-full h-24 px-3 py-2 bg-obsidian-900 border border-slate-700 rounded-lg text-white text-sm placeholder:text-slate-500 resize-none"
                          data-testid="batch-description"
                        />
                        <p className="text-slate-500 text-xs mt-2">
                          Images are processed in order. Each <code className="bg-obsidian-800 px-1 rounded">$</code> moves to the next image.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Single Image Process Button */}
              <Button
                onClick={handleProcess}
                disabled={!selectedImage || processing}
                className="w-full btn-gradient text-white rounded-full py-6 mt-6"
                data-testid="process-btn"
              >
                {processing ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5 mr-2" />
                    Process Image
                  </>
                )}
              </Button>

              {/* Batch Processing Buttons */}
              {images.length > 1 && (
                <div className="mt-4 space-y-3">
                  <div className="section-divider my-4" />
                  <p className="text-slate-400 text-sm text-center">
                    Batch Mode: {images.length} images
                  </p>
                  <Button
                    onClick={handleBatchProcess}
                    disabled={images.length === 0 || batchProcessing}
                    className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-full py-6"
                    data-testid="batch-process-btn"
                  >
                    {batchProcessing ? (
                      <>
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        Processing Batch...
                      </>
                    ) : (
                      <>
                        <Layers className="w-5 h-5 mr-2" />
                        Process All {images.length} Images
                      </>
                    )}
                  </Button>
                  
                  {images.some(img => img.processed_base64) && (
                    <Button
                      onClick={handleBatchDownload}
                      variant="outline"
                      className="w-full border-slate-700 text-white hover:bg-white/5 rounded-full py-6"
                      data-testid="batch-download-btn"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Download All as ZIP
                    </Button>
                  )}
                </div>
              )}
            </motion.div>

            {/* Batch Limits Info */}
            {images.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="glass-card rounded-2xl p-4 mt-6"
              >
                <p className="text-slate-400 text-xs">
                  <strong className="text-slate-300">Batch Limits:</strong> Max {MAX_BATCH_SIZE} images, {MAX_FILE_SIZE_MB}MB per file, {MAX_TOTAL_SIZE_MB}MB total
                </p>
              </motion.div>
            )}
          </div>

          {/* Right Column - Preview */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass-card rounded-2xl p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-outfit font-semibold text-lg text-white">
                  Preview
                </h3>
                {selectedImage?.processed_base64 && (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowPreview(!showPreview)}
                      className="border-slate-700 text-white"
                      data-testid="toggle-preview-btn"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      {showPreview ? "Show Original" : "Show Processed"}
                    </Button>
                  </div>
                )}
              </div>

              {selectedImage ? (
                <div className="space-y-4">
                  {/* Crop Mode Banner */}
                  {cropMode && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Crop className="w-4 h-4 text-blue-400" />
                        <span className="text-blue-400 text-sm">Crop Mode Active - Drag to select area</span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCancelCrop}
                          className="border-slate-700 text-white text-xs"
                          data-testid="cancel-crop-btn"
                        >
                          Cancel
                        </Button>
                        {cropSelection && cropSelection.width > 10 && (
                          <Button
                            size="sm"
                            onClick={handleApplyCrop}
                            disabled={applyingCrop}
                            className="btn-gradient text-white text-xs"
                            data-testid="apply-crop-btn"
                          >
                            {applyingCrop ? (
                              <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <Check className="w-3 h-3 mr-1" />
                            )}
                            Apply Crop
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Image Preview with Compare Slider / Crop Mode */}
                  <div 
                    id="crop-preview-container"
                    className={`relative aspect-video bg-obsidian-800 rounded-xl overflow-hidden ${cropMode ? 'cursor-crosshair' : ''}`}
                    onMouseDown={cropMode ? handleCropMouseDown : undefined}
                    onMouseMove={cropMode ? handleCropMouseMove : undefined}
                    onMouseUp={cropMode ? handleCropMouseUp : undefined}
                    onMouseLeave={cropMode ? handleCropMouseUp : undefined}
                  >
                    {showCompareSlider && selectedImage.processed_base64 && !cropMode ? (
                      <ReactCompareSlider
                        itemOne={
                          <ReactCompareSliderImage
                            src={`data:image/png;base64,${selectedImage.original_base64}`}
                            alt="Original"
                          />
                        }
                        itemTwo={
                          <ReactCompareSliderImage
                            src={`data:image/png;base64,${selectedImage.processed_base64}`}
                            alt="Processed"
                          />
                        }
                        className="h-full"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full w-full">
                        <img
                          src={`data:image/png;base64,${
                            showPreview && selectedImage.processed_base64 && !cropMode
                              ? selectedImage.processed_base64
                              : selectedImage.original_base64
                          }`}
                          alt="Preview"
                          className="max-w-full max-h-full object-contain select-none"
                          style={{ pointerEvents: cropMode ? 'none' : 'auto' }}
                          draggable={false}
                          data-testid="preview-image"
                        />
                      </div>
                    )}
                    
                    {/* Crop Selection Overlay */}
                    {cropMode && cropSelection && (
                      <>
                        {/* Darkened areas outside selection */}
                        <div 
                          className="absolute inset-0 bg-black/60 pointer-events-none"
                          style={{
                            clipPath: `polygon(
                              0% 0%, 
                              0% 100%, 
                              ${cropSelection.x}px 100%, 
                              ${cropSelection.x}px ${cropSelection.y}px, 
                              ${cropSelection.x + cropSelection.width}px ${cropSelection.y}px, 
                              ${cropSelection.x + cropSelection.width}px ${cropSelection.y + cropSelection.height}px, 
                              ${cropSelection.x}px ${cropSelection.y + cropSelection.height}px, 
                              ${cropSelection.x}px 100%, 
                              100% 100%, 
                              100% 0%
                            )`
                          }}
                        />
                        {/* Selection Border */}
                        <div
                          className="absolute border-2 border-blue-400 pointer-events-none"
                          style={{
                            left: cropSelection.x,
                            top: cropSelection.y,
                            width: cropSelection.width,
                            height: cropSelection.height,
                          }}
                        >
                          {/* Corner handles */}
                          <div className="absolute -top-1 -left-1 w-3 h-3 bg-blue-400 rounded-sm" />
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-400 rounded-sm" />
                          <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-blue-400 rounded-sm" />
                          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-blue-400 rounded-sm" />
                          {/* Size indicator */}
                          {cropSelection.width > 50 && cropSelection.height > 30 && (
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-blue-500/80 px-2 py-1 rounded text-xs text-white whitespace-nowrap">
                              {Math.round(cropSelection.width * (imageNaturalSize.width / (document.getElementById("crop-preview-container")?.offsetWidth || 1)))} × {Math.round(cropSelection.height * (imageNaturalSize.height / (document.getElementById("crop-preview-container")?.offsetHeight || 1)))}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    
                    {showPreview && selectedImage.processed_base64 && !showCompareSlider && !cropMode && (
                      <div className="absolute top-2 right-2 px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                        Processed
                      </div>
                    )}
                  </div>

                  {/* Quick Edit Toolbar */}
                  {selectedImage.processed_base64 && (
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant={showCompareSlider ? "default" : "outline"}
                        onClick={() => setShowCompareSlider(!showCompareSlider)}
                        className={showCompareSlider ? "bg-blue-600" : "border-slate-700 text-white"}
                        data-testid="compare-slider-btn"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Compare
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowPreview(!showPreview)}
                        className="border-slate-700 text-white"
                      >
                        {showPreview ? "Original" : "Processed"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleUndo}
                        className="border-slate-700 text-white"
                        data-testid="undo-btn"
                      >
                        <Undo2 className="w-3 h-3 mr-1" />
                        Undo
                      </Button>
                    </div>
                  )}

                  {/* Rotate & Flip Tools */}
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant={cropMode ? "default" : "outline"}
                      onClick={cropMode ? handleCancelCrop : handleStartCrop}
                      className={cropMode ? "bg-blue-600" : "border-slate-700 text-white"}
                      title="Crop Image"
                      data-testid="crop-btn"
                    >
                      <Crop className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRotate(-90)}
                      className="border-slate-700 text-white"
                      title="Rotate Left"
                      disabled={cropMode}
                      data-testid="rotate-left-btn"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRotate(90)}
                      className="border-slate-700 text-white"
                      title="Rotate Right"
                      disabled={cropMode}
                      data-testid="rotate-right-btn"
                    >
                      <RotateCw className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleFlip(true)}
                      className="border-slate-700 text-white"
                      title="Flip Horizontal"
                      disabled={cropMode}
                      data-testid="flip-h-btn"
                    >
                      <FlipHorizontal className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleFlip(false)}
                      className="border-slate-700 text-white"
                      title="Flip Vertical"
                      disabled={cropMode}
                      data-testid="flip-v-btn"
                    >
                      <FlipVertical className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Filter Presets */}
                  <div className="border-t border-slate-700/50 pt-4">
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className="w-full flex items-center justify-between text-white font-medium py-2"
                      data-testid="toggle-filters-btn"
                    >
                      <span className="flex items-center gap-2">
                        <Film className="w-4 h-4 text-cyan-400" />
                        Filter Presets
                      </span>
                      {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    
                    <AnimatePresence>
                      {showFilters && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="grid grid-cols-3 gap-2 pt-3">
                            {FILTER_PRESETS.map((filter) => (
                              <button
                                key={filter.name}
                                onClick={() => handleApplyFilter(filter.name)}
                                disabled={applyingFilter}
                                className={`px-3 py-2 rounded-lg text-xs font-medium text-white bg-gradient-to-r ${filter.color} hover:opacity-90 transition-opacity disabled:opacity-50`}
                                data-testid={`filter-${filter.name}`}
                              >
                                {filter.label}
                              </button>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Image Info */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Original Name:</span>
                      <span className="text-white truncate ml-2">{selectedImage.original_name}</span>
                    </div>
                    {selectedImage.new_name && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">AI Name:</span>
                        <span className="text-blue-400 truncate ml-2">{selectedImage.new_name}</span>
                      </div>
                    )}
                  </div>

                  {/* Download Section with Quality Slider */}
                  <div className="border-t border-slate-700/50 pt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-slate-400 text-sm">Download Quality</Label>
                      <span className="text-white text-sm font-medium">{downloadQuality}%</span>
                    </div>
                    <Slider
                      value={[downloadQuality]}
                      onValueChange={([v]) => setDownloadQuality(v)}
                      min={10}
                      max={100}
                      step={5}
                      className="w-full"
                      data-testid="quality-slider"
                    />
                    <div className="flex gap-3">
                      <Button
                        onClick={() => handleDownload(selectedImage.image_id, false)}
                        variant="outline"
                        className="flex-1 border-slate-700 text-white"
                        data-testid="download-original-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Original
                      </Button>
                      {selectedImage.processed_base64 && (
                        <Button
                          onClick={() => handleDownload(selectedImage.image_id, true)}
                          className="flex-1 btn-gradient text-white"
                          data-testid="download-processed-btn"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Processed
                        </Button>
                      )}
                    </div>
                    
                    {/* Save to Gallery Button */}
                    {user && selectedImage.processed_base64 && (
                      <Button
                        onClick={() => handleSaveToGallery()}
                        disabled={savingToGallery}
                        variant="outline"
                        className="w-full border-green-600/50 text-green-400 hover:bg-green-500/10 mt-3"
                        data-testid="save-to-gallery-btn"
                      >
                        {savingToGallery ? (
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Save to Gallery
                      </Button>
                    )}
                    {!user && selectedImage.processed_base64 && (
                      <p className="text-xs text-slate-500 text-center mt-2">
                        <button onClick={() => onNavigate("login")} className="text-blue-400 hover:underline">
                          Log in
                        </button> to save images to your gallery
                      </p>
                    )}
                  </div>

                  {/* Touch-Up Section */}
                  <div className="pt-4 border-t border-slate-700/50 mt-4">
                    <button
                      onClick={() => setShowTouchUp(!showTouchUp)}
                      className="w-full flex items-center justify-between text-white font-medium py-2"
                      data-testid="toggle-touchup-btn"
                    >
                      <span className="flex items-center gap-2">
                        <Wand2 className="w-4 h-4 text-amber-400" />
                        Magic Wand Touch-Up
                      </span>
                      {showTouchUp ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                    
                    <AnimatePresence>
                      {showTouchUp && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="pt-4 space-y-4">
                            {/* Usage indicator */}
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-slate-400">
                                {touchUpUsage.unlimited ? (
                                  <span className="text-green-400 flex items-center gap-1">
                                    <Crown className="w-3 h-3" /> Unlimited
                                  </span>
                                ) : (
                                  `${touchUpUsage.remaining}/${touchUpUsage.limit} adjustments left today`
                                )}
                              </span>
                              {!touchUpUsage.unlimited && touchUpUsage.remaining <= 3 && (
                                <button 
                                  onClick={() => onNavigate("register")}
                                  className="text-amber-400 hover:text-amber-300"
                                >
                                  Upgrade
                                </button>
                              )}
                            </div>

                            {/* Auto-Enhance Button */}
                            <Button
                              onClick={handleAutoEnhance}
                              disabled={applyingTouchUp || (!touchUpUsage.unlimited && touchUpUsage.remaining <= 0)}
                              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white rounded-lg"
                              data-testid="auto-enhance-btn"
                            >
                              {applyingTouchUp ? (
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Sparkles className="w-4 h-4 mr-2" />
                              )}
                              Auto-Enhance
                            </Button>

                            {/* Brightness Slider */}
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <Label className="text-slate-300 text-sm flex items-center gap-2">
                                  <Sun className="w-3 h-3" /> Brightness
                                </Label>
                                <span className="text-slate-500 text-xs">{(touchUpSettings.brightness * 100).toFixed(0)}%</span>
                              </div>
                              <Slider
                                value={[touchUpSettings.brightness]}
                                onValueChange={([v]) => setTouchUpSettings(prev => ({ ...prev, brightness: v }))}
                                min={0.5}
                                max={2}
                                step={0.05}
                                className="w-full"
                                data-testid="brightness-slider"
                              />
                            </div>

                            {/* Contrast Slider */}
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <Label className="text-slate-300 text-sm flex items-center gap-2">
                                  <Contrast className="w-3 h-3" /> Contrast
                                </Label>
                                <span className="text-slate-500 text-xs">{(touchUpSettings.contrast * 100).toFixed(0)}%</span>
                              </div>
                              <Slider
                                value={[touchUpSettings.contrast]}
                                onValueChange={([v]) => setTouchUpSettings(prev => ({ ...prev, contrast: v }))}
                                min={0.5}
                                max={2}
                                step={0.05}
                                className="w-full"
                                data-testid="contrast-slider"
                              />
                            </div>

                            {/* Saturation Slider */}
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <Label className="text-slate-300 text-sm flex items-center gap-2">
                                  <Palette className="w-3 h-3" /> Saturation
                                </Label>
                                <span className="text-slate-500 text-xs">{(touchUpSettings.saturation * 100).toFixed(0)}%</span>
                              </div>
                              <Slider
                                value={[touchUpSettings.saturation]}
                                onValueChange={([v]) => setTouchUpSettings(prev => ({ ...prev, saturation: v }))}
                                min={0}
                                max={2}
                                step={0.05}
                                className="w-full"
                                data-testid="saturation-slider"
                              />
                            </div>

                            {/* Warmth Slider */}
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <Label className="text-slate-300 text-sm flex items-center gap-2">
                                  <Thermometer className="w-3 h-3" /> Warmth
                                </Label>
                                <span className="text-slate-500 text-xs">
                                  {touchUpSettings.warmth > 0 ? `+${(touchUpSettings.warmth * 100).toFixed(0)}` : (touchUpSettings.warmth * 100).toFixed(0)}%
                                </span>
                              </div>
                              <Slider
                                value={[touchUpSettings.warmth]}
                                onValueChange={([v]) => setTouchUpSettings(prev => ({ ...prev, warmth: v }))}
                                min={-1}
                                max={1}
                                step={0.05}
                                className="w-full"
                                data-testid="warmth-slider"
                              />
                            </div>

                            {/* Apply & Reset Buttons */}
                            <div className="flex gap-2 pt-2">
                              <Button
                                onClick={handleResetTouchUp}
                                variant="outline"
                                size="sm"
                                className="flex-1 border-slate-700 text-white"
                                data-testid="reset-touchup-btn"
                              >
                                <RotateCcw className="w-3 h-3 mr-1" />
                                Reset
                              </Button>
                              <Button
                                onClick={handleTouchUpAdjust}
                                disabled={applyingTouchUp || (!touchUpUsage.unlimited && touchUpUsage.remaining <= 0)}
                                size="sm"
                                className="flex-1 btn-gradient text-white"
                                data-testid="apply-touchup-btn"
                              >
                                {applyingTouchUp ? (
                                  <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                  <Check className="w-3 h-3 mr-1" />
                                )}
                                Apply
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              ) : (
                <div className="aspect-video bg-obsidian-800 rounded-xl flex items-center justify-center">
                  <div className="text-center">
                    <ImageIcon className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                    <p className="text-slate-500">Select an image to preview</p>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
        )}

        {/* Gallery Tab Content */}
        {activeTab === "gallery" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-2xl p-6"
          >
            {/* Gallery Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-outfit font-semibold text-xl text-white">
                  My Gallery
                </h2>
                <p className="text-slate-400 text-sm">
                  {gallery.length} saved image{gallery.length !== 1 ? "s" : ""}
                </p>
              </div>
              {user && gallery.some(img => img.is_public) && (
                <Button
                  onClick={copyShareLink}
                  variant="outline"
                  className="border-slate-700 text-white"
                  data-testid="share-gallery-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share Gallery
                </Button>
              )}
            </div>

            {!user ? (
              <div className="text-center py-12">
                <FolderOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-white font-medium mb-2">Sign in to view your gallery</h3>
                <p className="text-slate-400 text-sm mb-4">
                  Save your edited images and access them anytime
                </p>
                <Button
                  onClick={() => onNavigate("login")}
                  className="btn-gradient text-white"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In
                </Button>
              </div>
            ) : galleryLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            ) : gallery.length === 0 ? (
              <div className="text-center py-12">
                <ImageIcon className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-white font-medium mb-2">No images in gallery</h3>
                <p className="text-slate-400 text-sm">
                  Process images in the Editor and click &quot;Save to Gallery&quot; to add them here
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Gallery Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {gallery.map((image) => (
                    <div
                      key={image.id}
                      className={`group relative aspect-square bg-obsidian-800 rounded-xl overflow-hidden cursor-pointer transition-all hover:ring-2 hover:ring-blue-500 ${
                        selectedGalleryImage?.id === image.id ? "ring-2 ring-blue-500" : ""
                      }`}
                      onClick={() => setSelectedGalleryImage(image)}
                      data-testid={`gallery-image-${image.id}`}
                    >
                      <img
                        src={`data:image/png;base64,${image.thumbnail_base64 || image.image_base64}`}
                        alt={image.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-2 left-2 right-2">
                          <p className="text-white text-xs truncate">{image.title}</p>
                          <div className="flex items-center gap-1 mt-1">
                            {image.is_public ? (
                              <Globe className="w-3 h-3 text-green-400" />
                            ) : (
                              <Lock className="w-3 h-3 text-slate-400" />
                            )}
                            <span className="text-slate-400 text-xs">
                              {image.is_public ? "Public" : "Private"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Selected Image Details */}
                {selectedGalleryImage && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border-t border-slate-700 pt-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Image Preview */}
                      <div className="aspect-video bg-obsidian-800 rounded-xl overflow-hidden flex items-center justify-center">
                        <img
                          src={`data:image/png;base64,${selectedGalleryImage.image_base64 || selectedGalleryImage.thumbnail_base64}`}
                          alt={selectedGalleryImage.title}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>

                      {/* Image Details */}
                      <div className="space-y-4">
                        <div>
                          <h3 className="text-white font-semibold text-lg">
                            {selectedGalleryImage.title}
                          </h3>
                          <p className="text-slate-400 text-sm">
                            Original: {selectedGalleryImage.original_name}
                          </p>
                        </div>

                        {selectedGalleryImage.operations_applied?.length > 0 && (
                          <div>
                            <p className="text-slate-400 text-sm mb-2">Operations:</p>
                            <div className="flex flex-wrap gap-2">
                              {selectedGalleryImage.operations_applied.map((op, i) => (
                                <span
                                  key={i}
                                  className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full"
                                >
                                  {op}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => handleTogglePublic(selectedGalleryImage.id, selectedGalleryImage.is_public)}
                            variant="outline"
                            size="sm"
                            className={`border-slate-700 ${selectedGalleryImage.is_public ? "text-green-400" : "text-slate-400"}`}
                            data-testid="toggle-public-btn"
                          >
                            {selectedGalleryImage.is_public ? (
                              <>
                                <Globe className="w-4 h-4 mr-2" />
                                Public
                              </>
                            ) : (
                              <>
                                <Lock className="w-4 h-4 mr-2" />
                                Private
                              </>
                            )}
                          </Button>

                          {selectedGalleryImage.is_public && (
                            <Button
                              onClick={() => {
                                const link = `${window.location.origin}?view=${selectedGalleryImage.id}`;
                                navigator.clipboard.writeText(link);
                                toast.success("Image link copied!");
                              }}
                              variant="outline"
                              size="sm"
                              className="border-slate-700 text-slate-400"
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              Copy Link
                            </Button>
                          )}
                        </div>

                        <div className="flex items-center gap-2 pt-4 border-t border-slate-700">
                          <Button
                            onClick={() => {
                              const link = document.createElement("a");
                              link.href = `data:image/png;base64,${selectedGalleryImage.image_base64}`;
                              link.download = selectedGalleryImage.title + ".png";
                              link.click();
                            }}
                            className="flex-1 btn-gradient text-white"
                            data-testid="gallery-download-btn"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                          <Button
                            onClick={() => {
                              if (confirm("Delete this image from your gallery?")) {
                                handleDeleteFromGallery(selectedGalleryImage.id);
                              }
                            }}
                            variant="outline"
                            className="border-red-600/50 text-red-400 hover:bg-red-500/10"
                            data-testid="gallery-delete-btn"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

// ============== LANDING PAGE SECTIONS ==============
const HeroSection = ({ onNavigate }) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef(null);

  // Stop video when component unmounts
  useEffect(() => {
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    };
  }, []);

  const toggleVideo = () => {
    const video = videoRef.current;
    if (video) {
      if (isVideoPlaying) {
        video.pause();
        setIsVideoPlaying(false);
      } else {
        video.play().catch(() => {
          // Handle autoplay restrictions gracefully
          setIsVideoPlaying(false);
        });
        setIsVideoPlaying(true);
      }
    }
  };

  const handleNavigate = (page) => {
    // Stop video before navigating
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setIsVideoPlaying(false);
    onNavigate(page);
  };

  return (
    <section className="hero-bg min-h-screen flex items-center pt-20" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="text-center max-w-4xl mx-auto"
        >
          <motion.div variants={fadeInUp} className="mb-8">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-medium">
              <Zap className="w-4 h-4" />
              Powered by Gemini 3 Flash
            </span>
          </motion.div>

          <motion.h1
            variants={fadeInUp}
            className="font-outfit font-bold text-4xl sm:text-5xl lg:text-7xl tracking-tight mb-6"
          >
            Transform Your Images{" "}
            <span className="gradient-text">with AI Intelligence</span>
          </motion.h1>

          <motion.p
            variants={fadeInUp}
            className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            Batch rename, remove backgrounds, and resize images with the power of AI. 
            Process hundreds of files in minutes, not hours.
          </motion.p>

          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Button
              size="lg"
              className="btn-gradient text-white rounded-full px-8 py-6 text-lg shadow-lg shadow-blue-500/25 group"
              onClick={() => handleNavigate("dashboard")}
              data-testid="hero-cta-primary"
            >
              Start Processing Free
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="rounded-full px-8 py-6 text-lg border-white/10 text-white hover:bg-white/5"
              onClick={() => document.getElementById("pricing").scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-cta-secondary"
            >
              View Pricing
            </Button>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            className="flex flex-wrap items-center justify-center gap-6"
          >
            <div className="trust-badge">
              <Clock className="w-4 h-4 text-blue-400" />
              <span>Process hundreds in minutes</span>
            </div>
            <div className="trust-badge">
              <Shield className="w-4 h-4 text-green-400" />
              <span>Your files stay private</span>
            </div>
            <div className="trust-badge">
              <FileImage className="w-4 h-4 text-violet-400" />
              <span>JPG, PNG, WEBP support</span>
            </div>
          </motion.div>

          {/* Demo Video */}
          <motion.div
            variants={fadeInUp}
            className="mt-16 relative max-w-3xl mx-auto"
          >
            <div className="relative rounded-2xl overflow-hidden bg-obsidian-800 shadow-2xl shadow-blue-500/10 border border-white/10">
              <video
                ref={videoRef}
                className="w-full aspect-video object-cover"
                loop
                muted
                playsInline
                poster="/demo-video-poster.jpg"
                onEnded={() => setIsVideoPlaying(false)}
              >
                <source src="/demo-video.mp4" type="video/mp4" />
              </video>
              
              {/* Play Button Overlay */}
              <div
                className={`absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity cursor-pointer ${
                  isVideoPlaying ? "opacity-0 hover:opacity-100" : "opacity-100"
                }`}
                onClick={toggleVideo}
                data-testid="video-play-overlay"
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30"
                >
                  {isVideoPlaying ? (
                    <Pause className="w-8 h-8 text-white" />
                  ) : (
                    <Play className="w-8 h-8 text-white ml-1" />
                  )}
                </motion.div>
              </div>

              {/* Video Label */}
              <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-black/50 backdrop-blur-sm rounded-full text-white text-xs font-medium">
                <Film className="w-3 h-3 inline mr-1.5" />
                Watch Demo
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-violet-500/20 rounded-full blur-[128px] pointer-events-none" />
    </section>
  );
};

const FeaturesSection = () => {
  const features = [
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "AI-Powered Renaming",
      description: "Gemini 3 Flash analyzes your images and generates descriptive 4-word filenames automatically.",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: <Layers className="w-6 h-6" />,
      title: "Background Removal",
      description: "Remove backgrounds instantly with AI segmentation. Export as transparent PNG.",
      gradient: "from-violet-500 to-purple-500"
    },
    {
      icon: <Maximize2 className="w-6 h-6" />,
      title: "Smart Resizing",
      description: "Batch resize images to exact dimensions or scale by percentage while maintaining aspect ratio.",
      gradient: "from-pink-500 to-rose-500"
    }
  ];

  return (
    <section id="features" className="py-24 md:py-32 relative" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4">
            Powerful Features for{" "}
            <span className="gradient-text">Every Creator</span>
          </h2>
          <p className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto">
            Everything you need to manage and transform your images at scale
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="feature-card glass-card rounded-2xl p-8 group"
              data-testid={`feature-card-${index}`}
            >
              <div className={`icon-container mb-6 bg-gradient-to-br ${feature.gradient} bg-opacity-20`}>
                <div className="text-white">{feature.icon}</div>
              </div>
              <h3 className="font-outfit font-semibold text-xl text-white mb-3">
                {feature.title}
              </h3>
              <p className="text-slate-400 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PricingSection = ({ onNavigate, user, token }) => {
  const PAYPAL_EMAIL = "calico5kid@prodigy.net";
  const API = process.env.REACT_APP_BACKEND_URL;
  const [loadingStripe, setLoadingStripe] = useState(null);
  
  const plans = [
    {
      name: "Starter",
      price: "$0",
      priceValue: 0,
      period: "/mo",
      description: "Perfect for trying out",
      features: [
        "50 credits/month",
        "5 actions per minute",
        "Up to 5 images per batch",
        "Community support"
      ],
      credits: 50,
      cta: "Start Free",
      popular: false,
      packageId: null
    },
    {
      name: "Pro",
      price: "$9",
      priceValue: 9,
      period: "/mo",
      description: "For serious creators",
      features: [
        "500 credits/month",
        "20 actions per minute",
        "Up to 15 images per batch",
        "Generous AI renames",
        "Priority support"
      ],
      credits: 500,
      cta: "Subscribe",
      popular: true,
      packageId: "pro"
    },
    {
      name: "Business",
      price: "$25",
      priceValue: 25,
      period: "/mo",
      description: "High-volume processing",
      features: [
        "2,000 credits/month",
        "50 actions per minute",
        "Up to 20 images per batch",
        "Generous everything",
        "Premium support",
        "API access"
      ],
      credits: 2000,
      cta: "Subscribe",
      popular: false,
      packageId: "business"
    }
  ];

  const handlePayPalPayment = (plan) => {
    const paypalUrl = new URL("https://www.paypal.com/cgi-bin/webscr");
    paypalUrl.searchParams.set("cmd", "_xclick-subscriptions");
    paypalUrl.searchParams.set("business", PAYPAL_EMAIL);
    paypalUrl.searchParams.set("item_name", `AI Image Suite - ${plan.name} Plan`);
    paypalUrl.searchParams.set("a3", plan.priceValue);
    paypalUrl.searchParams.set("p3", "1");
    paypalUrl.searchParams.set("t3", "M");
    paypalUrl.searchParams.set("src", "1");
    paypalUrl.searchParams.set("sra", "1");
    paypalUrl.searchParams.set("currency_code", "USD");
    paypalUrl.searchParams.set("no_note", "1");
    paypalUrl.searchParams.set("return", window.location.origin + "/dashboard?payment=success&plan=" + plan.name.toLowerCase());
    paypalUrl.searchParams.set("cancel_return", window.location.origin + "/#pricing");
    
    window.open(paypalUrl.toString(), "_blank");
  };

  const handleStripePayment = async (plan) => {
    if (!plan.packageId) return;
    
    setLoadingStripe(plan.packageId);
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/stripe/checkout`, {
        package_id: plan.packageId,
        origin_url: window.location.origin
      }, { headers });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error("Stripe checkout error:", error);
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    } finally {
      setLoadingStripe(null);
    }
  };

  return (
    <section id="pricing" className="py-24 md:py-32 relative" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4">
            Simple, Transparent{" "}
            <span className="gradient-text">Pricing</span>
          </h2>
          <p className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto">
            Start free and upgrade when you need more power
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-center">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`relative rounded-2xl p-8 ${
                plan.popular ? "pricing-popular glass-card" : "glass-card"
              }`}
              data-testid={`pricing-card-${plan.name.toLowerCase()}`}
            >
              {plan.popular && (
                <div className="pricing-badge">
                  <Star className="w-3 h-3 inline mr-1" />
                  Popular
                </div>
              )}
              
              <div className="mb-6">
                <h3 className="font-outfit font-semibold text-xl text-white mb-2">{plan.name}</h3>
                <p className="text-slate-400 text-sm">{plan.description}</p>
              </div>

              <div className="mb-6">
                <span className="font-outfit font-bold text-5xl text-white">{plan.price}</span>
                <span className="text-slate-400">{plan.period}</span>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-slate-300">
                    <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {plan.priceValue === 0 ? (
                <Button
                  className="w-full rounded-full py-6 bg-white/5 text-white border border-white/10 hover:bg-white/10"
                  onClick={() => onNavigate("dashboard")}
                  data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                >
                  {plan.cta}
                </Button>
              ) : (
                <div className="space-y-3">
                  {/* Stripe Button */}
                  <Button
                    className={`w-full rounded-full py-6 ${
                      plan.popular
                        ? "btn-gradient text-white shadow-lg shadow-blue-500/25"
                        : "bg-white/5 text-white border border-white/10 hover:bg-white/10"
                    }`}
                    onClick={() => handleStripePayment(plan)}
                    disabled={loadingStripe === plan.packageId}
                    data-testid={`pricing-stripe-${plan.name.toLowerCase()}`}
                  >
                    {loadingStripe === plan.packageId ? (
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    ) : (
                      <CreditCard className="w-5 h-5 mr-2" />
                    )}
                    Pay with Card
                  </Button>
                  
                  {/* PayPal Button */}
                  <Button
                    variant="outline"
                    className="w-full rounded-full py-6 border-white/10 text-white hover:bg-white/10"
                    onClick={() => handlePayPalPayment(plan)}
                    data-testid={`pricing-paypal-${plan.name.toLowerCase()}`}
                  >
                    <img 
                      src="https://www.paypalobjects.com/webstatic/icon/pp258.png" 
                      alt="PayPal" 
                      className="w-5 h-5 mr-2"
                    />
                    Pay with PayPal
                  </Button>
                </div>
              )}
            </motion.div>
          ))}
        </div>
        
        {/* Payment Trust Badge */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-4 px-6 py-3 rounded-full bg-obsidian-800/50 border border-white/5">
            <Shield className="w-5 h-5 text-green-400" />
            <span className="text-slate-400 text-sm">Secure payments via</span>
            <div className="flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-blue-400" />
              <span className="text-slate-300 text-sm">Stripe</span>
              <span className="text-slate-500">|</span>
              <img 
                src="https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-200px.png" 
                alt="PayPal" 
                className="h-5"
              />
            </div>
          </div>
        </motion.div>

        {/* How Credits Work Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="mt-16 max-w-4xl mx-auto"
        >
          <h3 className="font-outfit font-semibold text-2xl text-white text-center mb-8">
            How Credits Work
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Credit Costs Card */}
            <div className="glass-card rounded-2xl p-6">
              <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                Credit Costs
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">AI Rename</span>
                  <span className="text-white font-medium">1 credit</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Resize Image</span>
                  <span className="text-white font-medium">1 credit</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Apply Filter</span>
                  <span className="text-white font-medium">2 credits</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Touch-up Adjust</span>
                  <span className="text-white font-medium">3 credits</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Remove Background</span>
                  <span className="text-white font-medium">5 credits</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Smart Batch (AI grouping)</span>
                  <span className="text-white font-medium">10 credits</span>
                </div>
              </div>
            </div>

            {/* Fair Use Policy Card */}
            <div className="glass-card rounded-2xl p-6">
              <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-400" />
                Fair Use Policy
              </h4>
              <div className="space-y-4 text-slate-400 text-sm">
                <p>
                  Credits refresh monthly. Unused credits don&apos;t roll over, but you can 
                  purchase additional credits anytime at $0.10 each.
                </p>
                <p>
                  Rate limits help ensure fair access for everyone. Higher plans get 
                  faster processing and larger batch sizes.
                </p>
                <p className="text-slate-500 text-xs">
                  We use industry-standard fraud prevention to protect all users. 
                  Suspicious activity may result in temporary limitations.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-violet-500/10 rounded-full blur-[150px] pointer-events-none" />
    </section>
  );
};

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "How does AI-powered renaming work?",
      answer: "Our AI analyzes the visual content of your images using Gemini 3 Flash and generates descriptive, meaningful 4-word filenames. This makes your files easier to find and organize without manual effort."
    },
    {
      question: "What image formats are supported?",
      answer: "We support JPG, PNG, and WEBP formats for all our features. Background removal exports as transparent PNG for maximum flexibility."
    },
    {
      question: "Is my data secure?",
      answer: "Absolutely. Your images are processed securely and are stored temporarily in memory. They are deleted after you download them. We use encrypted connections and your files are never stored permanently."
    },
    {
      question: "Can I process images in bulk?",
      answer: "Yes! Our platform is designed for batch processing. You can upload and process multiple images at once, saving you hours of manual work."
    },
    {
      question: "Do I need an account to use the free tier?",
      answer: "No! You can use the free tier without creating an account. Simply start processing images right away. Create an account only if you want to upgrade to Pro or Elite."
    }
  ];

  return (
    <section id="faq" className="py-24 md:py-32" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4">
            Frequently Asked{" "}
            <span className="gradient-text">Questions</span>
          </h2>
          <p className="text-slate-400 text-base md:text-lg">
            Everything you need to know about AI Image Suite
          </p>
        </motion.div>

        <div className="glass-card rounded-2xl overflow-hidden">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="faq-item"
              data-testid={`faq-item-${index}`}
            >
              <button
                className="w-full px-6 py-5 flex items-center justify-between text-left"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                data-testid={`faq-toggle-${index}`}
              >
                <span className="font-medium text-white pr-4">{faq.question}</span>
                {openIndex === index ? (
                  <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                )}
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <p className="px-6 pb-5 text-slate-400 leading-relaxed">{faq.answer}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CTASection = ({ onNavigate }) => {
  return (
    <section className="py-24 md:py-32 cta-section" data-testid="cta-section">
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-6">
            Ready to Transform Your{" "}
            <span className="gradient-text">Workflow?</span>
          </h2>
          <p className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto mb-10">
            Join thousands of creators who save hours every week with AI Image Suite. 
            Start free, no credit card required.
          </p>
          <Button
            size="lg"
            className="btn-gradient text-white rounded-full px-10 py-6 text-lg shadow-lg shadow-blue-500/25 group"
            onClick={() => onNavigate("dashboard")}
            data-testid="final-cta-btn"
          >
            Start Processing Free
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
          <a
            href="https://aiimagesuites.pro"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-blue-400 transition-colors"
            data-testid="companion-app-link"
          >
            <Camera className="w-4 h-4" />
            Need to capture photos first? Open Smart Session Capture
            <ExternalLink className="w-3 h-3" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// ============== PRIVACY POLICY PAGE ==============
const PrivacyPolicyPage = ({ onNavigate }) => {
  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="privacy-policy-page">
      <div className="max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <button onClick={() => onNavigate("landing")} className="text-blue-400 hover:text-blue-300 text-sm mb-6 flex items-center gap-1" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>
          <h1 className="font-outfit font-bold text-3xl text-white mb-2">Privacy Policy</h1>
          <p className="text-slate-400 text-sm mb-8">Effective: June 1, 2026 • Version 1.0</p>

          <div className="space-y-8 text-slate-300 text-sm leading-relaxed">
            <section>
              <h2 className="font-outfit font-semibold text-lg text-white mb-3">Data We Collect</h2>
              <ul className="space-y-2">
                <li><strong className="text-white">Account Data:</strong> Email address, name, and hashed password when you create an account.</li>
                <li><strong className="text-white">Uploaded Images:</strong> Images you upload are stored temporarily during active sessions and persistently if saved to your gallery.</li>
                <li><strong className="text-white">Usage Data:</strong> Credit usage, processing actions, and timestamps for billing and abuse prevention.</li>
                <li><strong className="text-white">Technical Data:</strong> IP address, device info, and request metadata for security and rate limiting.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-outfit font-semibold text-lg text-white mb-3">How We Use Your Data</h2>
              <ul className="list-disc list-inside space-y-1">
                <li>To provide the image processing services you request</li>
                <li>To manage your account, credits, and subscriptions</li>
                <li>To detect and prevent abuse, fraud, and policy violations</li>
                <li>To improve our services and fix technical issues</li>
              </ul>
            </section>

            <section>
              <h2 className="font-outfit font-semibold text-lg text-white mb-3">Data Sharing</h2>
              <p>We do not sell your personal data. We share data only with payment processors (Stripe, PayPal) to complete transactions, and AI service providers for image renaming in anonymized form.</p>
            </section>

            <section>
              <h2 className="font-outfit font-semibold text-lg text-white mb-3">Data Retention</h2>
              <ul className="space-y-2">
                <li><strong className="text-white">Session Images:</strong> Deleted when your session ends or server restarts.</li>
                <li><strong className="text-white">Gallery Images:</strong> Stored until you delete them or close your account.</li>
                <li><strong className="text-white">Account Data:</strong> Retained while active; deleted within 30 days of account closure.</li>
                <li><strong className="text-white">Usage Logs:</strong> Retained for 90 days for abuse prevention.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-outfit font-semibold text-lg text-white mb-3">Your Rights</h2>
              <ul className="list-disc list-inside space-y-1">
                <li>Access your personal data at any time via your account</li>
                <li>Delete your gallery images and account data</li>
                <li>Export your data upon request</li>
                <li>Opt out of non-essential data processing</li>
              </ul>
            </section>

            <section>
              <h2 className="font-outfit font-semibold text-lg text-white mb-3">Children&apos;s Privacy</h2>
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                <p className="text-red-300">This service is not intended for anyone under 18. We do not knowingly collect data from minors. If we learn we have collected data from someone under 18, we will delete it immediately.</p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// ============== TERMS OF SERVICE PAGE ==============
const TermsOfServicePage = ({ onNavigate }) => {
  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="terms-of-service-page">
      <div className="max-w-3xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <button onClick={() => onNavigate("landing")} className="text-blue-400 hover:text-blue-300 text-sm mb-6 flex items-center gap-1" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>
          <h1 className="font-outfit font-bold text-3xl text-white mb-2">Terms of Service</h1>
          <p className="text-slate-400 text-sm mb-8">Effective: June 1, 2026 - Version 1.0</p>

          <div className="space-y-8 text-slate-300 text-sm leading-relaxed">
            <section>
              <h2 className="font-outfit font-semibold text-lg text-white mb-3">Acceptance</h2>
              <p>By creating an account or using AI Image Suite, you agree to these Terms of Service and our Content Policy. If you do not agree, do not use the service.</p>
            </section>

            <section>
              <h2 className="font-outfit font-semibold text-lg text-white mb-3">Age Requirement</h2>
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                <p className="text-amber-300">You must be at least 18 years old to use this service. By creating an account, you confirm you meet this requirement.</p>
              </div>
            </section>

            <section>
              <h2 className="font-outfit font-semibold text-lg text-white mb-3">Service Description</h2>
              <p>AI Image Suite provides AI-powered image processing tools including renaming, background removal, resizing, filters, cropping, touch-ups, and batch processing.</p>
            </section>

            <section id="content-policy">
              <h2 className="font-outfit font-semibold text-lg text-white mb-3">Content Policy</h2>
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-4">
                <h3 className="font-semibold text-red-300 mb-2">Strictly Prohibited Content</h3>
                <ul className="list-disc list-inside space-y-1 text-red-200/80">
                  <li>Images depicting minors (anyone under 18) in any context</li>
                  <li>Sexually explicit or suggestive content involving minors</li>
                  <li>Content that exploits or endangers children</li>
                  <li>Non-consensual intimate imagery of any person</li>
                  <li>Content that violates any applicable law</li>
                </ul>
              </div>
              <h3 className="font-semibold text-white mb-2">Your Obligations</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>You must be at least 18 years old to use this service</li>
                <li>You must not upload images containing minors</li>
                <li>You are solely responsible for the content you upload</li>
                <li>You must have the right or permission to use any images you upload</li>
                <li>You agree to immediate account termination for policy violations</li>
              </ul>
            </section>

            <section>
              <h2 className="font-outfit font-semibold text-lg text-white mb-3">Enforcement</h2>
              <ul className="list-disc list-inside space-y-1">
                <li>Accounts found in violation will be permanently suspended</li>
                <li>Uploaded content may be reviewed for policy compliance</li>
                <li>Violations may be reported to relevant authorities</li>
                <li>No refunds will be issued for terminated accounts</li>
              </ul>
            </section>

            <section>
              <h2 className="font-outfit font-semibold text-lg text-white mb-3">Intellectual Property</h2>
              <p>You retain ownership of images you upload. We claim no ownership over your content. You grant us a limited license to process your images as you request.</p>
            </section>

            <section>
              <h2 className="font-outfit font-semibold text-lg text-white mb-3">Account Termination</h2>
              <p>We reserve the right to suspend or terminate accounts that violate our content policy without notice or refund.</p>
            </section>

            <section>
              <h2 className="font-outfit font-semibold text-lg text-white mb-3">Limitation of Liability</h2>
              <p>The service is provided &quot;as is&quot;. We are not liable for any loss of data, service interruptions, or damages arising from use of the service.</p>
            </section>

            <section>
              <h2 className="font-outfit font-semibold text-lg text-white mb-3">Modifications</h2>
              <p>We may update these terms at any time. Continued use after changes constitutes acceptance of the updated terms.</p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const Footer = ({ onNavigate }) => {
  return (
    <footer className="border-t border-white/5 py-12 px-6">
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
            <Wand2 className="w-4 h-4 text-white" />
          </div>
          <span className="font-outfit font-bold text-white">AI Image Suite</span>
        </div>
        <div className="flex items-center gap-6 text-sm text-slate-400">
          <a href="#features" className="hover:text-white transition-colors">Features</a>
          <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
          <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
          <button onClick={() => onNavigate("privacy")} className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy</button>
          <button onClick={() => onNavigate("terms")} className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms</button>
        </div>
        <p className="text-slate-600 text-sm">2026 AI Image Suite. All rights reserved.</p>
      </div>
    </footer>
  );
};

// ============== MAIN APP ==============
function App() {
  const { user, token, loading, login, register, logout } = useAuth();
  const [currentPage, setCurrentPage] = useState("landing");

  // Check for payment success from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get("payment");
    const plan = urlParams.get("plan");
    const sessionId = urlParams.get("session_id");
    
    if (payment === "success") {
      setCurrentPage("dashboard");
      
      if (sessionId) {
        const pollStripeStatus = async (attempts = 0) => {
          const maxAttempts = 5;
          if (attempts >= maxAttempts) {
            toast.info("Payment status pending. Please check your email for confirmation.");
            return;
          }
          
          try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/stripe/status/${sessionId}`);
            
            if (response.data.payment_status === "paid") {
              toast.success(`Welcome to ${plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : "Pro"}! Your subscription is now active.`, {
                duration: 5000
              });
              if (user) {
                window.location.reload();
              }
            } else if (response.data.status === "expired") {
              toast.error("Payment session expired. Please try again.");
            } else {
              setTimeout(() => pollStripeStatus(attempts + 1), 2000);
            }
          } catch (error) {
            // Payment status check failed
          }
        };
        
        pollStripeStatus();
      } else if (plan) {
        toast.success(`Welcome to ${plan.charAt(0).toUpperCase() + plan.slice(1)}! Your subscription is being processed.`, {
          duration: 5000
        });
      }
      
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [user]);

  const handleNavigate = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian-950 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian-950 text-white" data-testid="app-root">
      <Toaster position="top-right" theme="dark" richColors />
      
      <Navbar 
        currentPage={currentPage} 
        user={user} 
        onNavigate={handleNavigate} 
        onLogout={logout}
      />
      
      <main>
        {currentPage === "landing" && (
          <>
            <HeroSection onNavigate={handleNavigate} />
            <FeaturesSection />
            <PricingSection onNavigate={handleNavigate} user={user} token={token} />
            <FAQSection />
            <CTASection onNavigate={handleNavigate} />
          </>
        )}
        
        {(currentPage === "login" || currentPage === "register") && (
          <AuthPage 
            onLogin={login}
            onRegister={register}
            onNavigate={handleNavigate}
            isLogin={currentPage === "login"}
          />
        )}
        
        {currentPage === "dashboard" && (
          <DashboardPage 
            user={user} 
            token={token}
            onNavigate={handleNavigate}
          />
        )}
        {currentPage === "privacy" && <PrivacyPolicyPage onNavigate={handleNavigate} />}
        {currentPage === "terms" && <TermsOfServicePage onNavigate={handleNavigate} />}
      </main>
      
      {(currentPage === "landing" || currentPage === "privacy" || currentPage === "terms") && <Footer onNavigate={handleNavigate} />}
    </div>
  );
}

export default App;